import {
  DashboardIcon,
  UsersIcon,
  AdminsIcon,
  TransactionsIcon,
  SubscriptionsIcon,
  CoursesIcon,
  AnalysisIcon,
  TicketsIcon,
  DiscountIcon,
  FaqIcon,
  MediaIcon,
  NotificationsIcon,
  DataIcon,
  AgentIcon,
  PortfolioIcon,
  ConsultingIcon,
} from '@/components/icons';
import { ACCESS_RULES, AdminAccessRule, hasAdminAccess } from '@/utils/adminAccess';

export interface NavigationItem {
  id: string;
  label: string;
  labelPersian: string;
  path: string;
  icon: React.ComponentType<{ className?: string; size?: number }>;
  permissions: string[];
  children?: NavigationItem[];
  requiredRoles?: string[];
  accessRule?: AdminAccessRule;
}

const privateStagingDisabledNavigation = new Set([
  'data',
  'courses',
  'analysis',
  'media',
  'tickets',
  'faq',
  'portfolio',
  'portfolio-management',
]);

const completeNavigationConfig: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    labelPersian: 'داشبورد',
    path: '/dashboard',
    icon: DashboardIcon,
    permissions: ['Self'], // Available to all authenticated admins
    requiredRoles: [],
    accessRule: ACCESS_RULES.dashboard,
  },
  {
    id: 'data',
    label: 'Data Management',
    labelPersian: 'مدیریت داده',
    path: '/data',
    icon: DataIcon,
    permissions: ['Admin', 'Data'],
    requiredRoles: ['admin', 'manager'],
    accessRule: ACCESS_RULES.data,
  },
  {
    id: 'options-management',
    label: 'Options Management',
    labelPersian: 'مدیریت آپشن',
    path: '/options-management',
    icon: DataIcon,
    permissions: ['Admin'],
    requiredRoles: ['admin', 'manager'],
    accessRule: ACCESS_RULES.optionsManagement,
  },
  {
    id: 'users',
    label: 'Users Management',
    labelPersian: 'مدیریت کاربران',
    path: '/users',
    icon: UsersIcon,
    permissions: ['Admin', 'Users'],
    requiredRoles: ['admin', 'manager'],
    accessRule: ACCESS_RULES.users,
    children: [
      {
        id: 'users-list',
        label: 'All Users',
        labelPersian: 'همه کاربران',
        path: '/users',
        icon: UsersIcon,
        permissions: ['Admin', 'Users'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.users,
      },
      {
        id: 'users-seminar',
        label: 'Seminar Users',
        labelPersian: 'کاربران سمینار',
        path: '/users/seminar',
        icon: UsersIcon,
        permissions: ['Admin', 'Users'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.users,
      },
    ],
  },
  {
    id: 'admins',
    label: 'Admin Management',
    labelPersian: 'مدیریت ادمین‌ها',
    path: '/admins',
    icon: AdminsIcon,
    permissions: ['Admin'],
    requiredRoles: ['superadmin', 'manager'],
    accessRule: ACCESS_RULES.admins,
  },
  {
    id: 'finance',
    label: 'Finance',
    labelPersian: 'امور مالی',
    path: '/finance',
    icon: TransactionsIcon,
    permissions: ['Admin', 'Finance'],
    requiredRoles: ['admin', 'manager'],
    accessRule: ACCESS_RULES.finance,
    children: [
      {
        id: 'transactions',
        label: 'Transactions',
        labelPersian: 'تراکنش‌ها',
        path: '/finance/transactions',
        icon: TransactionsIcon,
        permissions: ['Admin', 'Finance'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.financialTransactions,
      },
      {
        id: 'discount-codes',
        label: 'Discount Codes',
        labelPersian: 'کدهای تخفیف',
        path: '/finance/discounts',
        icon: DiscountIcon,
        permissions: ['Admin', 'Finance'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.financialDiscountCodes,
      },
    ],
  },
  {
    id: 'subscriptions',
    label: 'Subscriptions',
    labelPersian: 'اشتراک‌ها',
    path: '/subscriptions',
    icon: SubscriptionsIcon,
    permissions: ['Admin', 'Subscriptions'],
    requiredRoles: ['admin', 'manager'],
    accessRule: ACCESS_RULES.subscriptions,
  },
  {
    id: 'referrals',
    label: 'Referrals',
    labelPersian: 'سیستم رفرال',
    path: '/referrals',
    icon: UsersIcon,
    permissions: ['Admin'],
    requiredRoles: ['admin', 'manager'],
    accessRule: ACCESS_RULES.referrals,
  },
  {
    id: 'campaigns',
    label: 'Campaign Wheel',
    labelPersian: 'گردونه کمپین',
    path: '/campaigns',
    icon: DiscountIcon,
    permissions: ['Admin'],
    requiredRoles: ['admin', 'manager'],
    accessRule: ACCESS_RULES.campaigns,
  },
  {
    id: 'courses',
    label: 'Courses',
    labelPersian: 'دوره‌ها',
    path: '/courses',
    icon: CoursesIcon,
    permissions: ['Admin', 'Courses'],
    requiredRoles: ['admin', 'manager'],
    accessRule: ACCESS_RULES.courses,
    children: [
      {
        id: 'courses-list',
        label: 'All Courses',
        labelPersian: 'همه دوره‌ها',
        path: '/courses',
        icon: CoursesIcon,
        permissions: ['Admin', 'Courses'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.courses,
      },
      {
        id: 'course-categories',
        label: 'Categories',
        labelPersian: 'دسته‌بندی‌ها',
        path: '/courses/categories',
        icon: CoursesIcon,
        permissions: ['Admin', 'Courses'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.courseCategories,
      },
      {
        id: 'course-teachers',
        label: 'Teachers',
        labelPersian: 'اساتید',
        path: '/courses/teachers',
        icon: CoursesIcon,
        permissions: ['Admin', 'Courses'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.courseInstructors,
      },
      {
        id: 'course-orders',
        label: 'Course Orders',
        labelPersian: 'سفارشات دوره',
        path: '/courses/orders',
        icon: CoursesIcon,
        permissions: ['Admin', 'Courses'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.courseOrders,
      },
      {
        id: 'course-license',
        label: 'Course license',
        labelPersian: 'لایسنس دوره',
        path: '/courses/license',
        icon: CoursesIcon,
        permissions: ['Admin', 'Courses'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.courseLicense,
      },
    ],
  },
  {
    id: 'analysis',
    label: 'Analysis',
    labelPersian: 'تحلیل‌ها',
    path: '/analysis',
    icon: AnalysisIcon,
    permissions: ['Admin', 'Analysis'],
    requiredRoles: ['admin', 'manager'],
    accessRule: ACCESS_RULES.analyses,
  },
  {
    id: 'media',
    label: 'Media Center',
    labelPersian: 'مرکز رسانه',
    path: '/media',
    icon: MediaIcon,
    permissions: ['Admin', 'Media'],
    requiredRoles: ['admin', 'manager'],
    accessRule: ACCESS_RULES.media,
  },
  {
    id: 'tickets',
    label: 'Tickets',
    labelPersian: 'تیکت‌ها',
    path: '/tickets',
    icon: TicketsIcon,
    permissions: ['Admin', 'Tickets'],
    requiredRoles: ['admin', 'manager'],
    accessRule: ACCESS_RULES.tickets,
  },
  {
    id: 'public-support',
    label: 'Public Support',
    labelPersian: 'گفت‌وگوهای پشتیبانی',
    path: '/public-support',
    icon: TicketsIcon,
    permissions: ['public_support.view_all', 'public_support.view_assigned'],
    requiredRoles: ['admin', 'manager'],
    accessRule: ACCESS_RULES.publicSupport,
  },
  {
    id: 'consulting',
    label: 'Consulting',
    labelPersian: 'درخواست‌های مشاوره',
    path: '/consulting',
    icon: ConsultingIcon,
    permissions: ['consulting.manage'],
    requiredRoles: ['admin', 'manager'],
    accessRule: ACCESS_RULES.consulting,
  },
  {
    id: 'faq',
    label: 'FAQ',
    labelPersian: 'پرسش و پاسخ',
    path: '/faq',
    icon: FaqIcon,
    permissions: ['Admin', 'FAQ'],
    requiredRoles: ['admin', 'manager'],
    accessRule: ACCESS_RULES.faq,
    children: [
      {
        id: 'faq-list',
        label: 'All Questions',
        labelPersian: 'همه سوالات',
        path: '/faq',
        icon: FaqIcon,
        permissions: ['Admin', 'FAQ'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.faq,
      },
      {
        id: 'faq-categories',
        label: 'Categories',
        labelPersian: 'دسته‌بندی‌ها',
        path: '/faq/categories',
        icon: FaqIcon,
        permissions: ['Admin', 'FAQ'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.faq,
      },
    ],
  },
  {
    id: 'notifications',
    label: 'Notifications',
    labelPersian: 'اعلان‌ها',
    path: '/notifications',
    icon: NotificationsIcon,
    permissions: ['Admin'],
    requiredRoles: ['admin', 'manager'],
    accessRule: ACCESS_RULES.notifications,
  },
  {
    id: 'agent',
    label: 'Agent',
    labelPersian: 'هوش مصنوعی',
    path: '/ai/agent-overview',
    icon: AgentIcon,
    permissions: ['Admin'],
    requiredRoles: ['admin', 'manager'],
    accessRule: ACCESS_RULES.aiAgentGroup,
    children: [
      {
        id: 'agent-overview',
        label: 'Agent Overview',
        labelPersian: 'نمای کلی Agent',
        path: '/ai/agent-overview',
        icon: AgentIcon,
        permissions: ['ai_agent.view'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.aiAgentOverview,
      },
      {
        id: 'agent-users',
        label: 'Agent Users',
        labelPersian: 'کاربران و اعتبار',
        path: '/ai/agent-users',
        icon: UsersIcon,
        permissions: ['ai_agent.users.view'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.aiAgentUsers,
      },
      {
        id: 'agent-usage',
        label: 'Agent Usage',
        labelPersian: 'مصرف و هزینه',
        path: '/ai/agent-usage',
        icon: AnalysisIcon,
        permissions: ['ai_agent.usage.view'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.aiAgentUsage,
      },
      {
        id: 'agent-conversations',
        label: 'Agent Conversations',
        labelPersian: 'مکالمه‌ها',
        path: '/ai/agent-conversations',
        icon: AgentIcon,
        permissions: ['ai_agent.conversations.view'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.aiAgentConversations,
      },
      {
        id: 'agent-settings',
        label: 'Agent Settings',
        labelPersian: 'تنظیمات و سلامت',
        path: '/ai/agent-settings',
        icon: AgentIcon,
        permissions: ['ai_agent.settings.view', 'ai_agent.health.view', 'ai_agent.audit.view'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.aiAgentSettings,
      },
      {
        id: 'agent-rag',
        label: 'RAG Management',
        labelPersian: 'مدیریت دانش',
        path: '/agent/rag',
        icon: AgentIcon,
        permissions: ['Admin'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.rag,
      },
      {
        id: 'ai-credits',
        label: 'AI Credits',
        labelPersian: 'اعتبار هوش مصنوعی',
        path: '/ai-credits',
        icon: AgentIcon,
        permissions: ['credit.analytics.view'],
        requiredRoles: ['admin', 'manager'],
        accessRule: ACCESS_RULES.aiCredits,
      },
    ],
  },
  // {
  //   id: 'portfolio',
  //   label: 'Portfolio Management',
  //   labelPersian: 'سبدگردانی',
  //   path: '/portfolio',
  //   icon: PortfolioIcon,
  //   permissions: ['Admin', 'Portfolio'],
  //   requiredRoles: ['admin', 'manager'],
  //   accessRule: ACCESS_RULES.portfolio,
  // },
  {
    id: 'portfolio-management',
    label: 'Portfolio Management Definitions',
    labelPersian: 'مدیریت سبدگردانی',
    path: '/portfolio-management',
    icon: PortfolioIcon,
    permissions: [],
    requiredRoles: ['superadmin', 'manager'],
    accessRule: ACCESS_RULES.portfolioManagement,
  },
];

export const navigationConfig: NavigationItem[] =
  import.meta.env.VITE_PRIVATE_STAGING === 'true'
    ? completeNavigationConfig.filter(item => !privateStagingDisabledNavigation.has(item.id))
    : completeNavigationConfig;

/**
 * Check if user has permission to access a navigation item
 */
export const hasPermission = (
  item: NavigationItem,
  userPermissions: string[],
  userRole: string
): boolean => {
  if (item.accessRule) {
    return hasAdminAccess(
      {
        role: userRole,
        permissions: userPermissions,
      },
      item.accessRule
    );
  }

  // Check if user role is in required roles
  if (item.requiredRoles && item.requiredRoles.length > 0) {
    if (!item.requiredRoles.includes(userRole)) {
      return false;
    }
  }

  // Check if user has any of the required permissions
  return item.permissions.some(permission => userPermissions.includes(permission));
};

/**
 * Filter navigation items based on user permissions
 */
export const getFilteredNavigation = (
  navigation: NavigationItem[],
  userPermissions: string[],
  userRole: string
): NavigationItem[] => {
  return navigation
    .filter(item => hasPermission(item, userPermissions, userRole))
    .map(item => ({
      ...item,
      children: item.children
        ? getFilteredNavigation(item.children, userPermissions, userRole)
        : undefined,
    }))
    .filter(item => !item.children || item.children.length > 0);
};
